import {useState, MouseEvent, useMemo} from 'react';
import {Button, Menu, MenuItem, Skeleton, Stack, SxProps} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import AddIcon from '@mui/icons-material/Add';
import {useObserveViewport} from '../../../../../../../hooks';
import {FetchActivitiesSectorsAndGroups} from '../shared/use-fetch-activities-sectors-and-groups';
import {
  PlannerAddSelectionActivitiesSectorItem,
  PlannerAddSelectionAnniversaryItem,
  PlannerAddSelectionItem,
} from './planner-add-selection-item';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {
  PERSONAL_DEVELOPMENT_SECTOR_ID,
  SECTORS_IDS,
  SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING,
} from '../../../../../../../utils/constants/hardcoded';
import {Tooltip} from '../../../../../../../components/tooltip/tooltip';

export type PlannerAddMenuButtonProps = {
  activitiesSectorsAndGroupsData: FetchActivitiesSectorsAndGroups;
  onSelected?: (item: PlannerAddSelectionItem) => void;
};

export function PlannerAddMenuButton(props: PlannerAddMenuButtonProps) {
  const {t} = useTranslation();
  const {hasWritePermission, state} = useAuth();
  const {isRunningCloseToMobile: isGettingCloseToMobile, isRunningMobile} = useObserveViewport();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Filter activities sectors according to user permissions
  const disabledSectorsItems = useMemo(() => {
    const items = props.activitiesSectorsAndGroupsData.items?.filter(
      (x) => !hasWritePermission(SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING[x.sector.id!])
    );

    return items;
  }, [props.activitiesSectorsAndGroupsData.items, hasWritePermission]);

  const handleClose = (evt: MouseEvent<HTMLElement>) => {
    setAnchorEl(null);
    const target = evt.target as HTMLElement;
    if (target.id) {
      const activitiesSectorAndGroups = props.activitiesSectorsAndGroupsData.items?.find(
        (item) => item.sector.id === target.id
      );

      // Activities sector item
      if (activitiesSectorAndGroups) {
        const sectorItem: PlannerAddSelectionActivitiesSectorItem = {
          type: 'activities-sector',
          data: activitiesSectorAndGroups,
        };
        props.onSelected?.(sectorItem);
      }
      // Anniversary item
      else if (target.id === 'anniversary') {
        const anniversaryItem: PlannerAddSelectionAnniversaryItem = {
          type: 'anniversary',
          data: undefined,
        };
        props.onSelected?.(anniversaryItem);
      }
      // Unknown item
      else {
        console.error('Unable to select. Unknown item:', target);
      }
    }
  };

  const menuSx: SxProps = {
    padding: isRunningMobile ? '20px 10px' : '10px 6px',
    color: 'gray.main',
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '10px',
    '&:hover': {
      backgroundColor: 'blue.hover',
    },
  };

  return (
    <>
      <Button
        aria-controls={open ? 'planner-add-btn' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="inverted"
        size="large"
        onClick={handleClick}
        startIcon={<AddIcon />}
        sx={{
          width: 'auto',
          py: '8px',
          textTransform: 'uppercase',
          '& .MuiButton-startIcon': {
            margin: isGettingCloseToMobile ? 0 : undefined,
          },
        }}
      >
        {!isGettingCloseToMobile && t(translations.planner.header.add)}
      </Button>
      <Menu
        id="planner-add-btn"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        sx={{
          marginTop: '5px',
          '.MuiList-root': {
            width: isRunningMobile ? '100vw' : '160px',
            padding: '4px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          },
        }}
      >
        {/* --------- LOADING --------- */}
        {props.activitiesSectorsAndGroupsData.isLoading && (
          <Stack gap="10px" marginX="6px">
            <Skeleton variant="text" height="28px" width="30%" animation="wave" />
            <Skeleton variant="text" height="28px" width="30%" animation="wave" />
            <Skeleton variant="text" height="28px" width="60%" animation="wave" />
            <Skeleton variant="text" height="28px" width="80%" animation="wave" />
            {/* <Skeleton variant="text" height="28px" width="60%" animation="wave" /> */}
          </Stack>
        )}

        {/* --------- ITEMS --------- */}
        {!props.activitiesSectorsAndGroupsData.isLoading &&
          props.activitiesSectorsAndGroupsData.items?.map((item) => {
            const hasDCMContract =
              state.session?.selectedAffiliation?.contractsOverview?.includesDigitalCareManagement;
            const isDCMSector =
              item.sector.id !== PERSONAL_DEVELOPMENT_SECTOR_ID &&
              SECTORS_IDS.includes(item.sector.id!);
            const isDisabledByDCMContract = isDCMSector && !hasDCMContract;
            const isDisabled = disabledSectorsItems?.includes(item);

            const showTooltip = isDisabled || isDisabledByDCMContract;
            const tooltipTitle = isDisabledByDCMContract
              ? t(translations.office.access.tooltip)
              : t(translations.planner.header.add_disabled);

            return (
              <Tooltip.NotAllowedIf
                condition={showTooltip}
                title={
                  <Trans
                    values={{
                      module: t(translations.office.access['care_management']),
                    }}
                  >
                    {tooltipTitle}
                  </Trans>
                }
                key={item.sector.id}
              >
                <MenuItem
                  key={item.sector.id}
                  id={item.sector.id}
                  onClick={isDisabledByDCMContract ? undefined : handleClose}
                  sx={menuSx}
                  disabled={isDisabled}
                  style={{
                    textDecoration: isDisabledByDCMContract ? 'line-through' : undefined,
                  }}
                >
                  {item.sector.name}
                </MenuItem>
              </Tooltip.NotAllowedIf>
            );
          })}
        {/* {!props.activitiesSectorsData.isLoading && (
          <MenuItem id="anniversary" onClick={handleClose} sx={menuSx}>
            Anniversário
          </MenuItem>
        )} */}
      </Menu>
    </>
  );
}
